import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Icon from 'react-feather';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { endOfDay, startOfDay } from 'date-fns';

import AdminHeader from '../../components/header/AdminHeader';
import FormDateInput from '../../components/form/FormDateInput';

import CallexLoader from '../../components/CallexLoader';
import EditFollowUpModal from '../TenantAdmin/Configuration/EditFollowUpModal';
import { followUpListGet, followUpStatusUpdate } from '../../store/slices/callingSlice';
import { useUserDetails } from '../../store/slices/authSlice';
import { setIsDialNumber } from '../../store/slices/dialSlice';
import { convertToMask, formatPayloadDate } from '../../utils/common';
import { useTimezone } from '../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

const FollowUp = () => {
  const { t } = useTranslation();
  const timezone = useTimezone();
  const user = useUserDetails();
  const dispatch = useDispatch();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({ data: [], header: [] });
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    from_date: startOfDay(today),
    to_date: endOfDay(today)
  };

  const validationSchema = Yup.object({
    from_date: Yup.date().required('Start date is required'),
    to_date: Yup.date().required('End date is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.from_date);
      const formattedEndDate = formatPayloadDate(values.to_date);
      const payload = { ...values };
      payload['from_date'] = formattedStartDate;
      payload['to_date'] = formattedEndDate;

      const response = await dispatch(followUpListGet(payload)).unwrap();
      if (response) {
        setReportData({ data: response?.data || [], header: response?.header || [] });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleBlur, setFieldValue, handleSubmit, touched, errors } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    onSubmit();
  }, [values?.limit, values?.page]);

  return (
    <div>
      <AdminHeader
        title={t('followUp')}
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="from_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label={t('fromDate')}
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.from_date}
                  onChange={(e) => setFieldValue('from_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="to_date"
                  label={t('toDate')}
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.to_date}
                  onChange={(e) => setFieldValue('to_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit" disabled={isLoading}>
                {t('search')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr
                      key={index}
                      style={{
                        background:
                          val?.follow_up_status === 'done'
                            ? '#2cabe333'
                            : val?.follow_up_status === 'pending'
                              ? '#ff505033'
                              : '#ffb22b33'
                      }}>
                      {reportData?.header?.map((value, idx) => {
                        if (value?.name === 'phone_number') {
                          return (
                            <td key={idx}>
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                {user?.process_details?.numberMasking === 'true'
                                  ? convertToMask(val[value?.name] || '-')
                                  : val[value?.name] || '-'}{' '}
                                {['send notification', 'agent not available']?.includes(
                                  val['follow_up_status']
                                ) && (
                                  <Icon.PhoneForwarded
                                    size={14}
                                    className="cursor-pointer text-green"
                                    onClick={async () => {
                                      await dispatch(
                                        followUpStatusUpdate({
                                          follow_up_status: 1,
                                          id: val['id']
                                        })
                                      ).unwrap();
                                      dispatch(setIsDialNumber(val[value?.name]));
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                          );
                        }
                        if (value?.name === 'follow_up_status') {
                          return (
                            <td key={idx} style={{ textTransform: 'capitalize' }}>
                              {val[value?.name] || '-'}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div className="cursor-pointer text-info">
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                setEditRecord(val);
                                setIsEdit(true);
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          {t('noDataAvailable')}
        </Card>
      )}
      {isEdit && (
        <EditFollowUpModal
          isOpen={isEdit}
          isEdit
          showUser={false}
          editRecord={editRecord}
          onCloseClick={(apiCall = false) => {
            setEditRecord(null);
            setIsEdit(false);
            if (apiCall) {
              onSubmit();
            }
          }}
        />
      )}
    </div>
  );
};

export default FollowUp;
